import { manifestApi } from "#/api/manifestApi";
import type { HawbOption } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface HawbSelectProps {
  value: number | null;
  onChange: (hawbId: number | null, option: HawbOption | null) => void;
  /** Only list consignments that still have untagged items (tag linking). */
  hasUntagged?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  clearable?: boolean;
}

/** "123A · 235-11111111 · 32/50 tagged" */
const optionLabel = (option: HawbOption, tagged: string): string =>
  `${formatHawbLabel(option)} · ${option.tagged_count}/${option.packages} ${tagged}`;

/** Searchable consignment picker (AWB or HAWB number), server-side search. */
const HawbSelect = ({
  value,
  onChange,
  hasUntagged = false,
  label,
  placeholder,
  required,
  disabled,
  error,
  clearable = false,
}: HawbSelectProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [selected, setSelected] = useState<HawbOption | null>(null);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["hawb-options", debouncedSearch, hasUntagged],
    queryFn: async () => {
      const response = await manifestApi.getOptions({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(hasUntagged ? { has_untagged: true } : {}),
      });
      return response.data;
    },
  });

  // Keep the chosen option listed even when the current search hides it.
  const all = selected && !options.some((o) => o.id === selected.id) ? [selected, ...options] : options;
  const tagged = t("manifests.picker.tagged");

  return (
    <Select
      label={label}
      placeholder={placeholder ?? t("manifests.picker.placeholder")}
      required={required}
      disabled={disabled}
      error={error}
      clearable={clearable}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      filter={({ options: list }) => list}
      nothingFoundMessage={isFetching ? t("manifests.picker.loading") : t("manifests.picker.empty")}
      data={all.map((option) => ({ value: String(option.id), label: optionLabel(option, tagged) }))}
      value={value == null ? null : String(value)}
      onChange={(next) => {
        const option = next ? (all.find((o) => String(o.id) === next) ?? null) : null;
        setSelected(option);
        onChange(option ? option.id : null, option);
      }}
    />
  );
};

export default HawbSelect;
